import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Percent,
  TrendingUp,
  Lock,
  RocketLaunch,
  Visibility,
  Support,
  Public,
  Handshake,
  Hotel,
  Villa,
  HomeWork,
  Apartment,
  Star,
  Savings,
} from "@mui/icons-material";
import BlogPublicLayout from "../../components/blog/BlogPublicLayout";
import { BLOG_HERO_IMAGE } from "../../config/blogPosts";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-48px" },
  transition: { duration: 0.45 },
};

const benefits = [
  {
    icon: Percent,
    title: "0% commission for 30 days",
    text: "Keep more from every qualifying booking during your launch month on TabTrips.",
  },
  {
    icon: TrendingUp,
    title: "Earn more during the offer",
    text: "Maximize profits while you ramp up occupancy and guest reviews.",
  },
  {
    icon: Lock,
    title: "Secure prepaid bookings",
    text: "Trusted prepaid flow so you receive confirmed, paid reservations.",
  },
  {
    icon: RocketLaunch,
    title: "Easy property onboarding",
    text: "List hotels, resorts, villas, and homestays with guided extranet setup.",
  },
  {
    icon: Visibility,
    title: "Better online visibility",
    text: "Reach travellers searching for trusted stays across India and globally.",
  },
  {
    icon: Support,
    title: "Dedicated partner support",
    text: "Our team helps you through listing, rates, and day-to-day extranet tools.",
  },
  {
    icon: Public,
    title: "India & global travellers",
    text: "Tap into growing online travel demand with a modern OTA platform.",
  },
  {
    icon: Handshake,
    title: "Built for every stay type",
    text: "From luxury hotels to budget homestays — one platform, flexible inventory.",
  },
];

const propertyTypes = [
  { icon: Hotel, label: "Hotels" },
  { icon: Villa, label: "Resorts" },
  { icon: HomeWork, label: "Homestays" },
  { icon: HomeWork, label: "Guest houses" },
  { icon: Villa, label: "Villas" },
  { icon: Apartment, label: "Service apartments" },
  { icon: Star, label: "Boutique properties" },
  { icon: Star, label: "Luxury stays" },
  { icon: Savings, label: "Budget hotels" },
  { icon: HomeWork, label: "Vacation rentals" },
];

const ZeroPercentCommissionOffer = () => {
  const navigate = useNavigate();

  return (
    <BlogPublicLayout>
      <article>
        {/* Hero */}
        <header className="relative overflow-hidden bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-2 lg:items-center lg:gap-12">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/blog"
                className="mb-4 inline-flex items-center text-sm font-medium text-orange-100 no-underline hover:text-white"
              >
                ← Back to blog
              </Link>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-orange-100">
                Partners · May 2026 · 5 min read
              </p>
              <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-[2.35rem]">
                Earn Unlimited with TabTrips — 0 Percent Commission Offer
              </h1>
              <p className="mt-4 text-base leading-relaxed text-orange-50/95 sm:text-lg">
                The online hotel booking industry is growing rapidly, and travellers are
                continuously searching for trusted and affordable stays online. TabTrips is giving
                hotel owners, resorts, villas, homestays, and guest houses a special opportunity to
                grow their business with a limited-time{" "}
                <strong className="font-semibold text-white">30 Days 0 Percent Commission Offer</strong>.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative"
            >
              <img
                src={BLOG_HERO_IMAGE}
                alt="TabTrips 0% commission offer for new partner properties"
                className="w-full rounded-2xl border-4 border-white/20 shadow-2xl"
                width={640}
                height={400}
              />
            </motion.div>
          </div>
        </header>

        {/* Body */}
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-14">
          <motion.section {...fadeUp} className="prose prose-gray max-w-none">
            <p className="text-lg leading-relaxed text-gray-700">
              For every new property signup on TabTrips, hotel partners can enjoy zero commission
              for the first 30 days. This special launch offer helps properties increase bookings,
              improve online visibility, and maximize earnings during the offer period.
            </p>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14">
            <h2 className="text-2xl font-bold text-gray-900">Why join TabTrips</h2>
            <p className="mt-4 leading-relaxed text-gray-700">
              TabTrips is designed to help hotels connect with more travellers through a smart,
              secure, and easy-to-use booking platform. With growing online travel demand, having
              your property listed on a trusted OTA platform can help increase occupancy and attract
              more customers.
            </p>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14">
            <h2 className="text-2xl font-bold text-gray-900">Benefits of joining TabTrips</h2>
            <ul className="mt-6 space-y-3 text-gray-700">
              {[
                "0 percent commission for the first 30 days",
                "Opportunity to earn more during the offer period",
                "Secure prepaid booking system",
                "Easy property onboarding",
                "Better online visibility for hotels and stays",
                "Support for hotels, resorts, villas, apartments, and homestays",
                "Access to travellers across India and globally",
                "Dedicated partner support",
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.section>
        </div>

        {/* Benefit cards */}
        <section className="bg-gradient-to-b from-orange-50/80 to-white px-4 py-14 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <motion.div {...fadeUp} className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Everything you need to grow
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-gray-600">
                The 30-day zero commission offer gives hotel owners a great opportunity to maximize
                profits from bookings received during the promotional period.
              </p>
            </motion.div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {benefits.map(({ icon: Icon, title, text }, i) => (
                <motion.div
                  key={title}
                  {...fadeUp}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="rounded-2xl border border-orange-100/90 bg-white p-5 shadow-sm"
                >
                  <div className="mb-3 inline-flex rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 p-2.5 text-white">
                    <Icon fontSize="small" />
                  </div>
                  <h3 className="font-bold text-gray-900">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-14">
          <motion.section {...fadeUp}>
            <h2 className="text-2xl font-bold text-gray-900">Earn unlimited with TabTrips</h2>
            <p className="mt-4 leading-relaxed text-gray-700">
              Whether you own a luxury hotel, business property, mountain resort, beach villa, or
              budget stay, TabTrips helps you reach more travellers and grow faster online. With
              increasing travel demand in 2026, this is the perfect time to expand your online
              presence and attract more guests through TabTrips.
            </p>
          </motion.section>

          <motion.section {...fadeUp} className="mt-14">
            <h2 className="text-2xl font-bold text-gray-900">Simple and fast property signup</h2>
            <p className="mt-4 leading-relaxed text-gray-700">
              Getting started with TabTrips is quick and easy. Property owners can complete the
              signup process in simple steps and start receiving bookings after successful
              onboarding.
            </p>
          </motion.section>
        </div>

        {/* Property types */}
        <section className="border-y border-gray-200 bg-white px-4 py-14 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <motion.h2 {...fadeUp} className="text-center text-2xl font-bold text-gray-900">
              Property types we support
            </motion.h2>
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
              {propertyTypes.map(({ icon: Icon, label }, i) => (
                <motion.div
                  key={label}
                  {...fadeUp}
                  transition={{ delay: i * 0.04 }}
                  className="flex flex-col items-center rounded-xl border border-gray-100 bg-gray-50 px-3 py-5 text-center"
                >
                  <Icon className="text-orange-600" />
                  <span className="mt-2 text-sm font-semibold text-gray-800">{label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-14">
          <motion.section {...fadeUp}>
            <h2 className="text-2xl font-bold text-gray-900">Grow your property with TabTrips</h2>
            <p className="mt-4 leading-relaxed text-gray-700">
              TabTrips is focused on building a modern travel platform that helps hotel owners grow
              their business with better visibility, smart booking technology, and trusted customer
              support. The limited-time{" "}
              <strong>30 Days 0 Percent Commission Offer</strong> is the perfect opportunity for
              properties to join TabTrips, increase bookings, and experience the benefits of a
              growing OTA platform.
            </p>
          </motion.section>
        </div>

        {/* CTA */}
        <section className="px-4 pb-16 sm:px-6">
          <motion.div
            {...fadeUp}
            className="mx-auto max-w-3xl rounded-2xl bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 p-8 text-center text-white shadow-xl sm:p-10"
          >
            <h2 className="text-2xl font-bold sm:text-3xl">Ready to expand your hotel business?</h2>
            <p className="mx-auto mt-3 max-w-lg text-orange-50/95">
              Register your property on TabTrips Extranet and start with 30 days of zero platform
              commission on your new listing.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate("/listing")}
                className="w-full rounded-xl bg-white px-6 py-3.5 text-base font-bold text-orange-600 shadow-lg transition hover:bg-orange-50 sm:w-auto"
              >
                Partner registration
              </button>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full rounded-xl border-2 border-white/90 bg-transparent px-6 py-3.5 text-base font-semibold text-white transition hover:bg-white/10 sm:w-auto"
              >
                Partner login
              </button>
            </div>
            <p className="mt-6 text-sm text-orange-100/90">
              Prefer browsing first? Visit{" "}
              <a
                href="https://tabtrips.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-white underline underline-offset-2"
              >
                TabTrips.com
              </a>{" "}
              — tap, travel, go.
            </p>
          </motion.div>
        </section>
      </article>
    </BlogPublicLayout>
  );
};

export default ZeroPercentCommissionOffer;
